import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Lock, ArrowLeft, ShieldAlert, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECRET_STORAGE_KEY = 'fv_admin_secret_v1';

export default function AdminRefundPage() {
  const [secret, setSecret] = useState('');
  const [chargeId, setChargeId] = useState(() => new URLSearchParams(window.location.search).get('charge') || '');
  const [reason, setReason] = useState('requested_by_customer');
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [reverseTransfer, setReverseTransfer] = useState(true);
  const [refundFee, setRefundFee] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const cached = localStorage.getItem(SECRET_STORAGE_KEY);
    if (cached) setSecret(cached);
  }, []);

  const persistSecret = (v) => {
    setSecret(v);
    if (v) localStorage.setItem(SECRET_STORAGE_KEY, v);
    else localStorage.removeItem(SECRET_STORAGE_KEY);
  };

  const canSubmit = secret.trim().length > 20 && chargeId.trim().startsWith('ch_');

  const runRefund = async () => {
    setBusy(true);
    setResult(null);
    try {
      const body = {
        secret: secret.trim(),
        charge_id: chargeId.trim(),
        reverse_transfer: reverseTransfer,
        refund_application_fee: refundFee,
      };
      const stripeReasons = ['duplicate', 'fraudulent', 'requested_by_customer'];
      body.reason = stripeReasons.includes(reason) && !note.trim()
        ? reason
        : (note.trim() || reason);
      if (amount.trim() && Number(amount) > 0) body.amount = Number(amount);

      const res = await axios.post(`${API}/admin/refund`, body, { timeout: 60000 });
      setResult({ ok: true, data: res.data });
      if (res.data.status === 'already_refunded') {
        toast.info('Already fully refunded per Stripe. No action taken.');
      } else {
        toast.success(`Refunded $${res.data.refund_amount_dollars?.toFixed(2)} successfully.`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      const status = err?.response?.status;
      setResult({ ok: false, error: detail, status });
      toast.error(`Refund failed: ${detail}`);
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-vault" data-testid="admin-refund-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-[#9333EA] flex items-center justify-center">
                <Lock className="h-4 w-4 text-white" />
              </div>
              <span className="font-heading text-lg font-bold text-white tracking-tight">FutureVault Admin</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-[#A19DB0] hover:text-white hover:bg-white/5" data-testid="admin-refund-back">
                <ArrowLeft className="h-4 w-4 mr-2" /> Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <ShieldAlert className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-black text-white tracking-tight" data-testid="admin-refund-heading">Issue a Refund</h1>
            <p className="text-sm text-[#A19DB0]">Atomic Stripe refund + transfer reversal + fee refund.</p>
          </div>
        </div>

        <div className="rounded-lg bg-[#111017] border border-white/10 p-5 space-y-4">
          <div>
            <Label htmlFor="secret" className="text-white text-sm">Admin Secret</Label>
            <Input
              id="secret"
              type="password"
              placeholder="JWT_SECRET (stored locally after first entry)"
              value={secret}
              onChange={(e) => persistSecret(e.target.value)}
              data-testid="admin-refund-secret-input"
              className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#6F6981] font-mono text-sm"
            />
          </div>

          <div>
            <Label htmlFor="chargeId" className="text-white text-sm">Stripe Charge ID</Label>
            <Input
              id="chargeId"
              placeholder="ch_3U1W2kCrFIMUhJWs1XnykPca"
              value={chargeId}
              onChange={(e) => setChargeId(e.target.value)}
              data-testid="admin-refund-charge-input"
              className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#6F6981] font-mono text-sm"
            />
            <p className="mt-1 text-xs text-[#6F6981]">Find in Stripe Dashboard → Payments. Starts with <code>ch_</code>.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="reason" className="text-white text-sm">Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger id="reason" data-testid="admin-refund-reason-trigger" className="mt-1 bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1722] border-white/10 text-white">
                  <SelectItem value="requested_by_customer" data-testid="admin-refund-reason-customer">Customer request</SelectItem>
                  <SelectItem value="duplicate" data-testid="admin-refund-reason-duplicate">Duplicate</SelectItem>
                  <SelectItem value="fraudulent" data-testid="admin-refund-reason-fraud">Fraudulent</SelectItem>
                  <SelectItem value="other" data-testid="admin-refund-reason-other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="amount" className="text-white text-sm">Partial Amount (optional)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="Leave blank for full refund"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="admin-refund-amount-input"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#6F6981]"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="note" className="text-white text-sm">Note (goes into Stripe metadata)</Label>
            <Textarea
              id="note"
              rows={2}
              placeholder="Optional free-form note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="admin-refund-note-input"
              className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#6F6981]"
            />
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <label className="flex items-center gap-2 text-sm text-[#D4D1E0] cursor-pointer">
              <input
                type="checkbox"
                checked={reverseTransfer}
                onChange={(e) => setReverseTransfer(e.target.checked)}
                data-testid="admin-refund-reverse-transfer"
                className="accent-[#9333EA]"
              />
              Reverse the transfer (pull money back from Connect account) — <span className="text-amber-400">keep this ON in 99% of cases</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-[#D4D1E0] cursor-pointer">
              <input
                type="checkbox"
                checked={refundFee}
                onChange={(e) => setRefundFee(e.target.checked)}
                data-testid="admin-refund-refund-fee"
                className="accent-[#9333EA]"
              />
              Also refund the platform application fee
            </label>
          </div>

          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!canSubmit || busy}
            data-testid="admin-refund-submit-btn"
            className="w-full h-11 bg-[#9333EA] hover:bg-[#7c26cf] text-white font-semibold disabled:opacity-40"
          >
            {busy ? (<><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Processing…</>) : 'Issue Refund'}
          </Button>
        </div>

        {result && (
          <div
            className={`mt-6 rounded-lg border p-5 ${result.ok ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-rose-500/5 border-rose-500/30'}`}
            data-testid="admin-refund-result"
          >
            <div className="flex items-center gap-2 mb-3">
              {result.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <XCircle className="h-5 w-5 text-rose-400" />}
              <span className="font-semibold text-white">{result.ok ? (result.data.status === 'already_refunded' ? 'Already refunded' : 'Refund succeeded') : `Failed (${result.status || 'network'})`}</span>
            </div>
            {result.ok && result.data.status === 'refunded' && (
              <div className="text-sm text-[#D4D1E0] space-y-1 font-mono">
                <div>refund_id:  {result.data.stripe_refund_id}</div>
                <div>amount:     ${result.data.refund_amount_dollars?.toFixed(2)}</div>
                {result.data.goal_update && (
                  <>
                    <div className="pt-2 text-white">Vault updated:</div>
                    <div>  current:      ${result.data.goal_update.previous_current_amount} → ${result.data.goal_update.new_current_amount}</div>
                    <div>  withdrawable: ${result.data.goal_update.previous_withdrawable} → ${result.data.goal_update.new_withdrawable}</div>
                  </>
                )}
              </div>
            )}
            {!result.ok && (
              <div className="text-sm text-rose-300 font-mono">{result.error}</div>
            )}
          </div>
        )}
      </main>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#1A1722] border-white/10 text-white" data-testid="admin-refund-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Refund</DialogTitle>
            <DialogDescription className="text-[#A19DB0]">
              You&apos;re about to refund <span className="text-white font-mono">{chargeId}</span>
              {amount ? <> for <span className="text-white">${Number(amount).toFixed(2)}</span></> : <> for the <span className="text-white">full remaining amount</span></>}.
              This calls Stripe atomically and will {reverseTransfer ? 'pull the money back from the user\'s Connect account' : 'NOT reverse the transfer'}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} data-testid="admin-refund-cancel-btn" className="text-[#A19DB0] hover:text-white hover:bg-white/5">Cancel</Button>
            <Button onClick={runRefund} disabled={busy} data-testid="admin-refund-confirm-btn" className="bg-[#9333EA] hover:bg-[#7c26cf] text-white">
              {busy ? 'Refunding…' : 'Yes, refund now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
